import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import type { WorkflowResponse } from '../types';
import { describeExecutionStats } from '../utils/execution-stats-text';
import { getTriggerTypeLabel } from '../utils/node-labels';
import { WorkflowStatusBadge } from './WorkflowStatusBadge';

/**
 * Card de workflow (view "Cards"). Sem checkbox de seleção (F34 — seleção só
 * na view Lista): quick toggle de status (play/pause) + menu ••• de ações.
 * Arrastável para mover entre pastas (drag `workflow-id`).
 */
export interface WorkflowCardProps {
  workflow: WorkflowResponse;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onActivate: (id: string) => void;
  onPause: (id: string) => void;
  onExport: (id: string) => void;
}

export const WorkflowCard: React.FC<WorkflowCardProps> = ({
  workflow,
  onEdit,
  onDuplicate,
  onDelete,
  onActivate,
  onPause,
  onExport,
}) => {
  // Vários gatilhos por workflow (23/09/2026): o selo mostra o primeiro e "+N".
  const triggers = workflow.definition?.nodes?.filter((n) => n.type.startsWith('trigger_')) ?? [];
  const triggerType = triggers[0]?.type;
  const extraTriggers = triggers.length - 1;
  const nodeCount = workflow.definition?.nodes?.length || 0;
  const isActive = workflow.status === 'active';
  // U2 / D19: os contadores são opt-in (`?withStats=true`). Sem eles a tela não
  // escreve NADA sobre execução — "Nunca executou" em todo card seria mentira.
  const executionLines = workflow.executionStats
    ? describeExecutionStats(workflow.executionStats)
    : null;

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('workflow-id', workflow.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const actions: CardAction[] = [
    { label: 'Editar', icon: 'edit', onClick: () => onEdit(workflow.id) },
    { label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(workflow.id) },
    { label: 'Exportar', icon: 'download', onClick: () => onExport(workflow.id) },
    {
      label: isActive ? 'Desativar' : 'Ativar',
      icon: isActive ? 'pause' : 'play',
      onClick: () => (isActive ? onPause(workflow.id) : onActivate(workflow.id)),
    },
    { label: 'Excluir', icon: 'trash', onClick: () => onDelete(workflow.id), variant: 'danger' },
  ];

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors cursor-grab active:cursor-grabbing"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      draggable
      onDragStart={handleDragStart}
      data-testid={`workflow-card-${workflow.id}`}
    >
      {/* Header: nome + toggle status + ações (F34: card sem checkbox) */}
      <div className="flex items-start gap-2.5">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {workflow.name}
          </h3>
          {workflow.description && (
            <p
              className="text-xs mt-1 line-clamp-2"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {workflow.description}
            </p>
          )}
        </div>

        {/* Quick toggle ativar/pausar — rascunho/arquivado não têm (igual ao statusBtn do mockup) */}
        {(isActive || workflow.status === 'inactive') && (
          <button
            type="button"
            onClick={() => (isActive ? onPause(workflow.id) : onActivate(workflow.id))}
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-none transition-colors"
            style={
              /* tints reais do mapper são `-subtle` (a var `-background` não existe —
                 fundo ficava transparente e o pill do mockup sumia) */
              isActive
                ? { color: 'var(--theme-warning-fg)', backgroundColor: 'var(--theme-warning-subtle)' }
                : { color: 'var(--theme-success-fg)', backgroundColor: 'var(--theme-success-subtle)' }
            }
            title={isActive ? 'Pausar' : 'Ativar'}
            aria-label={isActive ? 'Pausar' : 'Ativar'}
            data-testid={`workflow-toggle-${workflow.id}`}
          >
            <Icon name={isActive ? 'pause' : 'play'} className="w-3.5 h-3.5" />
          </button>
        )}

        <div className="flex-none -mr-1 -mt-1">
          <CardActionsMenu actions={actions} size="sm" data-testid={`workflow-actions-${workflow.id}`} />
        </div>
      </div>

      {/* Badges: gatilho + status + etapas */}
      <div className="mt-3 flex items-center flex-wrap gap-2">
        {triggerType && (
          <span
            className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-md text-xs font-semibold"
            style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
          >
            <Icon name="zap" className="w-3 h-3" />
            {getTriggerTypeLabel(triggerType)}
            {extraTriggers > 0 && (
              <span style={{ color: 'var(--theme-muted-text)' }} data-testid={`workflow-extra-triggers-${workflow.id}`}>
                +{extraTriggers}
              </span>
            )}
          </span>
        )}
        <WorkflowStatusBadge status={workflow.status} autoPause={workflow.autoPause} data-testid={`workflow-card-status-${workflow.id}`} />
        <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }}>
          <Icon name="activity" className="w-3 h-3" />
          {nodeCount} {nodeCount === 1 ? 'etapa' : 'etapas'}
        </span>
      </div>

      {/* Execuções (U2 / D19): linha 1 = última execução; linha 2 = contagens da
          janela, com o trecho de falhas em destaque quando houver falha.
          `break-words` porque este card é também o que o celular vê. */}
      {executionLines && (
        <div className="mt-2 flex flex-col gap-0.5 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {/* `<p>` e não `<span>`: elemento de bloco mede largura de verdade —
              o teste de celular confere que o texto quebrou em vez de vazar. */}
          <p className="break-words" data-testid={`workflow-card-last-execution-${workflow.id}`}>
            {executionLines.noneInWindow === null
              ? executionLines.lastExecution
              : `${executionLines.lastExecution} — ${executionLines.noneInWindow}`}
          </p>
          {executionLines.counts !== null && executionLines.failures !== null && (
            <p className="break-words" data-testid={`workflow-card-execution-counts-${workflow.id}`}>
              {executionLines.counts}
              {' · '}
              <span
                data-testid={`workflow-card-failures-${workflow.id}`}
                style={{
                  color: executionLines.hasFailures
                    ? 'var(--theme-error-fg)'
                    : 'var(--theme-muted-text)',
                }}
              >
                {executionLines.failures}
              </span>
              {' '}
              {executionLines.windowSuffix}
            </p>
          )}
        </div>
      )}

      {/* Footer: atualizado + abrir editor */}
      <div
        className="mt-3 pt-3 flex items-center justify-between text-xs"
        style={{ borderTop: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
      >
        <span className="inline-flex items-center gap-1">
          <Icon name="clock" className="w-3 h-3" />
          {/* mockup: tempo RELATIVO ("Atualizado há 5 min") */}
          Atualizado {formatDistanceToNow(new Date(workflow.updatedAt), { addSuffix: true, locale: ptBR })}
        </span>
        <button
          onClick={() => onEdit(workflow.id)}
          className="font-semibold hover:underline"
          style={{ color: 'var(--theme-primary)' }}
          data-testid={`workflow-open-${workflow.id}`}
        >
          Abrir editor
        </button>
      </div>
    </div>
  );
};

WorkflowCard.displayName = 'WorkflowCard';
